/**
 * Campaigns — M5 marketing module.
 *
 * Header stats (Running / Scheduled / Sent 30d / Blocked 30d), campaign
 * table with lifecycle actions, a details drawer (stats + send ledger) and
 * a 3-step "New campaign" wizard (basics → audience → cadence steps).
 *
 * Data: campaignsService (+ analyticsService.campaignAttribution for the
 * 30-day send counters). MOCK_* fallbacks keep the page rendering when the
 * backend is unreachable.
 */
import { useMemo, useState } from 'react';
import { formatDateTime } from '../lib/datetime';
import TopBar from '../components/TopBar';
import { campaignsService, analyticsService } from '../lib/services';
import { useApi, useMutation } from '../lib/hooks';
import { useToast } from '../lib/toast';
import { useCustomerOptions } from '../lib/useDirectory';
import {
  EMPTY_AUDIENCE_MSG, campaignErrorMessage, firstEmptyStep, isEmptyAudience,
} from '../lib/campaignErrors';

const TYPE_PILL = { ONE_OFF: 'info', LIFECYCLE: 'accent' };
const STATUS_PILL = {
  DRAFT: '', SCHEDULED: 'accent', RUNNING: 'success',
  PAUSED: 'warning', COMPLETED: 'info', CANCELLED: 'danger',
};
const TRIGGER_EVENTS = [
  'NONE', 'BIRTHDAY', 'REGO_RENEWAL', 'SERVICE_DUE',
  'FINANCE_TERM_END', 'DELIVERY_ANNIVERSARY',
];
const STEP_CHANNELS = ['EMAIL', 'SMS', 'WHATSAPP'];
const SEND_STATUSES = ['QUEUED', 'SENT', 'BLOCKED_COMPLIANCE', 'FAILED', 'SKIPPED'];
const LEAD_SOURCES = [
  'CARSALES', 'DRIVE', 'CAREXPERT', 'AUTOTRADER', 'FACEBOOK_MARKETPLACE',
  'PHONE', 'WEB_FORM', 'EMAIL', 'WALK_IN', 'WHATSAPP', 'SMS',
];
const SEND_COLORS = {
  queued:  'var(--info)',
  sent:    'var(--success)',
  blocked: 'var(--warning)',
  failed:  'var(--danger)',
  skipped: 'var(--text-tertiary)',
};

/* Lifecycle buttons per campaign status: [label, action, variant, pastTense]. */
const LIFECYCLE_ACTIONS = {
  DRAFT:     [['Launch', 'launch', 'primary', 'Launched']],
  SCHEDULED: [['Launch', 'launch', 'primary', 'Launched'], ['Cancel', 'cancel', '', 'Cancelled']],
  RUNNING:   [['Pause', 'pause', '', 'Paused'], ['Cancel', 'cancel', 'danger', 'Cancelled']],
  PAUSED:    [['Resume', 'resume', 'primary', 'Resumed'], ['Cancel', 'cancel', 'danger', 'Cancelled']],
  COMPLETED: [],
  CANCELLED: [],
};

const fmtDate = (iso) => formatDateTime(iso);
const fmtNum  = (n)   => (n == null ? '0' : Number(n).toLocaleString('en-AU'));

/* ── Mock fallbacks — page must render without a backend ── */
const MOCK_CAMPAIGNS = [
  {
    id: 'mock-c1', name: 'EOFY Runout Blast', type: 'ONE_OFF', triggerEvent: 'NONE',
    status: 'RUNNING', createdAt: '2026-06-12T03:00:00Z',
    steps: [{ stepOrder: 0 }, { stepOrder: 1 }],
  },
  {
    id: 'mock-c2', name: 'Service Due Nurture', type: 'LIFECYCLE', triggerEvent: 'SERVICE_DUE',
    status: 'SCHEDULED', createdAt: '2026-06-28T03:00:00Z',
    steps: [{ stepOrder: 0 }, { stepOrder: 1 }, { stepOrder: 2 }],
  },
  {
    id: 'mock-c3', name: 'Birthday Wishes', type: 'LIFECYCLE', triggerEvent: 'BIRTHDAY',
    status: 'PAUSED', createdAt: '2026-05-02T03:00:00Z',
    steps: [{ stepOrder: 0 }],
  },
  {
    id: 'mock-c4', name: 'Winter Sale Teaser', type: 'ONE_OFF', triggerEvent: 'NONE',
    status: 'DRAFT', createdAt: '2026-07-08T03:00:00Z',
    steps: [{ stepOrder: 0 }, { stepOrder: 1 }],
  },
];
// Empty fallbacks — shown only while the real call is loading or if it errors.
// They are deliberately zeroed so the UI never displays fabricated figures;
// real data replaces them as soon as the API responds.
const MOCK_STATS = {
  queued: 0, sent: 0, blocked: 0, failed: 0, skipped: 0,
  delivered: 0, replies: 0, appointmentsGenerated: 0, optOuts: 0,
  byStep: [], byChannel: [],
};
const MOCK_SENDS = {
  content: [], page: 0, totalPages: 0, totalElements: 0, first: true, last: true,
};
const MOCK_ATTRIBUTION = { available: true, campaigns: [] };

export default function Campaigns() {
  const { toast } = useToast();
  const [selectedId, setSelectedId] = useState(null);
  const [wizardOpen, setWizardOpen] = useState(false);

  const { data: page, refetch } = useApi(
    () => campaignsService.list({ size: 100 }),
    { fallback: { content: MOCK_CAMPAIGNS }, deps: [] }
  );
  const campaigns = useMemo(
    () => (Array.isArray(page) ? page : page?.content || []),
    [page]
  );

  const window30 = useMemo(() => ({
    from: new Date(Date.now() - 30 * 86400000).toISOString(),
    to:   new Date().toISOString(),
  }), []);
  const { data: attribution } = useApi(
    () => analyticsService.campaignAttribution(window30),
    { fallback: MOCK_ATTRIBUTION, deps: [] }
  );

  const runningCount   = campaigns.filter((c) => c.status === 'RUNNING').length;
  const scheduledCount = campaigns.filter((c) => c.status === 'SCHEDULED').length;
  const sent30    = (attribution?.campaigns || []).reduce((s, c) => s + (c.sent    || 0), 0);
  const blocked30 = (attribution?.campaigns || []).reduce((s, c) => s + (c.blocked || 0), 0);

  const launch = useMutation(campaignsService.launch);
  const pause  = useMutation(campaignsService.pause);
  const resume = useMutation(campaignsService.resume);
  const cancel = useMutation(campaignsService.cancel);
  const lifecycle = { launch, pause, resume, cancel };
  const lifecycleBusy = launch.mutating || pause.mutating || resume.mutating || cancel.mutating;

  const onLifecycle = async (action, label, campaign) => {
    if (action === 'cancel' &&
        !window.confirm(`Cancel campaign "${campaign.name}"? Queued sends will be skipped.`)) {
      return;
    }
    if (action === 'launch') {
      // Check the audience first, so an empty one is explained up front instead
      // of surfacing as a bare 400 from the launch call. If the preview itself
      // is unavailable, fall through and let the server decide.
      try {
        const audience = await campaignsService.previewSegment({ segmentQuery: campaign.segmentQuery || {} });
        if (isEmptyAudience(audience)) {
          toast(`${label} blocked: ${EMPTY_AUDIENCE_MSG}`, { variant: 'warning' });
          return;
        }
      } catch {
        /* preview unavailable — launch will validate server-side */
      }
    }
    try {
      await lifecycle[action].mutate(campaign.id);
      toast(`${label} "${campaign.name}"`, { variant: 'success' });
      refetch();
    } catch (err) {
      toast(`${label} failed: ${campaignErrorMessage(err)}`, { variant: 'danger' });
    }
  };

  const selected = campaigns.find((c) => c.id === selectedId) || null;

  const headerStats = [
    { label: 'RUNNING',        value: runningCount,     color: 'var(--success)' },
    { label: 'SCHEDULED',      value: scheduledCount,   color: 'var(--accent-primary)' },
    { label: 'SENT (30D)',     value: fmtNum(sent30),   color: 'var(--info)' },
    { label: 'BLOCKED (30D)',  value: fmtNum(blocked30), color: 'var(--warning)' },
  ];

  return (
    <>
      <TopBar
        crumbs={['Campaigns']}
        right={
          <>
            <button className="btn small" onClick={refetch}>↻ Refresh</button>
            <button className="btn small primary" onClick={() => setWizardOpen(true)}>
              + New campaign
            </button>
          </>
        }
      />
      <div className="app-content">
        <div className="page-header">
          <div>
            <div className="page-header-title">Campaigns</div>
            <div className="page-header-sub">Segments, cadence steps and guarded sends</div>
          </div>
        </div>

        {/* ── Header stats ── */}
        <div className="grid-4" style={{ marginBottom: 16 }}>
          {headerStats.map((s) => (
            <div key={s.label} className="card">
              <div className="card-label">{s.label}</div>
              <div className="metric-value" style={{ color: s.color }}>{s.value}</div>
            </div>
          ))}
        </div>

        {/* ── Campaign table ── */}
        <div className="card" style={{ padding: 0 }}>
          <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>
                {['Name', 'Type', 'Trigger', 'Status', 'Steps', 'Created', 'Actions'].map((h) => (
                  <th key={h}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {campaigns.length === 0 && (
                <tr>
                  <td colSpan={7} className="text-muted" style={{ padding: 14, textAlign: 'center' }}>
                    No campaigns yet — create one to get started.
                  </td>
                </tr>
              )}
              {campaigns.map((c) => (
                <tr key={c.id} onClick={() => setSelectedId(c.id)} style={{ cursor: 'pointer' }}>
                  <td style={{ fontWeight: 600 }}>{c.name}</td>
                  <td><span className={`pill ${TYPE_PILL[c.type] || ''} filled`}>{c.type || '—'}</span></td>
                  <td className="mono text-xs">{c.triggerEvent || 'NONE'}</td>
                  <td><span className={`pill ${STATUS_PILL[c.status] || ''} filled`}>{c.status}</span></td>
                  <td className="mono text-xs">{c.steps ? c.steps.length : '—'}</td>
                  <td className="mono text-xs">{fmtDate(c.createdAt)}</td>
                  <td onClick={(e) => e.stopPropagation()}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      {(LIFECYCLE_ACTIONS[c.status] || []).map(([label, action, variant, past]) => (
                        <button
                          key={action}
                          className={`btn small ${variant}`}
                          disabled={lifecycleBusy}
                          onClick={() => onLifecycle(action, past, c)}
                        >{label}</button>
                      ))}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>

        {selected && (
          <CampaignDrawer
            campaign={selected}
            onClose={() => setSelectedId(null)}
            onLifecycle={onLifecycle}
            lifecycleBusy={lifecycleBusy}
          />
        )}

        {wizardOpen && (
          <CampaignWizard
            onClose={() => setWizardOpen(false)}
            onCreated={() => { setWizardOpen(false); refetch(); }}
          />
        )}
      </div>
    </>
  );
}

/* ── Horizontal stacked bar for a send-counter row ── */
function SendBar({ counts }) {
  const keys = ['queued', 'sent', 'blocked', 'failed', 'skipped'];
  const total = keys.reduce((s, k) => s + (counts[k] || 0), 0) || 1;
  return (
    <div style={{ display: 'flex', height: 10, borderRadius: 5, overflow: 'hidden',
                  background: 'var(--bg-glass)', border: '1px solid var(--border-color)' }}>
      {keys.map((k) => (counts[k] || 0) > 0 && (
        <div
          key={k}
          title={`${k}: ${fmtNum(counts[k])}`}
          style={{ width: `${(100 * counts[k]) / total}%`, background: SEND_COLORS[k] }}
        />
      ))}
    </div>
  );
}

/* ── Campaign details drawer: stats + paged send ledger + lifecycle ── */
function CampaignDrawer({ campaign, onClose, onLifecycle, lifecycleBusy }) {
  const [sendStatus, setSendStatus] = useState('');
  const [sendPage, setSendPage] = useState(0);
  const { options: customerOptions } = useCustomerOptions();
  const customerName = useMemo(() => {
    const m = new Map(customerOptions.map(o => [o.id, o.primary]));
    return (id, fb) => fb || m.get(id) || (id ? `#${String(id).slice(0, 8)}` : '—');
  }, [customerOptions]);

  const { data: stats } = useApi(
    () => campaignsService.stats(campaign.id),
    { fallback: MOCK_STATS, deps: [campaign.id] }
  );
  const { data: sends } = useApi(
    () => campaignsService.sends(campaign.id, {
      status: sendStatus || undefined, page: sendPage, size: 10,
    }),
    { fallback: MOCK_SENDS, deps: [campaign.id, sendStatus, sendPage] }
  );
  const sendRows = sends?.content || [];

  const totals = [
    ['Queued',  stats?.queued,  SEND_COLORS.queued],
    ['Sent',    stats?.sent,    SEND_COLORS.sent],
    ['Blocked', stats?.blocked, SEND_COLORS.blocked],
    ['Failed',  stats?.failed,  SEND_COLORS.failed],
  ];

  // CS14 outcome metrics — recipients' activity attributed to this campaign.
  const outcomes = [
    ['Delivered',    stats?.delivered,             SEND_COLORS.sent],
    ['Replies',      stats?.replies,               'var(--info)'],
    ['Appointments', stats?.appointmentsGenerated, 'var(--success)'],
    ['Opt-outs',     stats?.optOuts,               SEND_COLORS.blocked],
  ];

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Campaign ${campaign.name}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: 'min(620px, 96vw)',
          background: 'var(--bg-secondary)', borderLeft: '1px solid var(--border-color)',
          overflowY: 'auto', padding: 20, display: 'flex', flexDirection: 'column', gap: 16,
        }}
      >
        <div className="flex-between">
          <div>
            <div style={{ fontSize: 16, fontWeight: 600 }}>{campaign.name}</div>
            <div className="mono text-xs text-muted" style={{ marginTop: 2 }}>
              {campaign.type || '—'} · trigger {campaign.triggerEvent || 'NONE'} · created {fmtDate(campaign.createdAt)}
            </div>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        {/* Lifecycle actions for the current status */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span className={`pill ${STATUS_PILL[campaign.status] || ''} filled`}>{campaign.status}</span>
          {(LIFECYCLE_ACTIONS[campaign.status] || []).map(([label, action, variant, past]) => (
            <button
              key={action}
              className={`btn small ${variant}`}
              disabled={lifecycleBusy}
              onClick={() => onLifecycle(action, past, campaign)}
            >{label}</button>
          ))}
        </div>

        {/* Stats */}
        <div className="card">
          <div className="card-label">OUTCOMES</div>
          <div className="grid-4" style={{ marginTop: 8 }}>
            {outcomes.map(([l, v, c]) => (
              <div key={l} style={{ textAlign: 'center' }}>
                <div className="mono text-xs text-muted">{l.toUpperCase()}</div>
                <div className="metric-value" style={{ fontSize: 22, color: c }}>{fmtNum(v)}</div>
              </div>
            ))}
          </div>
          <div className="mono text-xs text-muted" style={{ marginTop: 6, opacity: 0.8 }}>
            Replies, appointments and opt-outs are attributed to recipients within 14 days of contact.
          </div>

          <div className="card-label" style={{ marginTop: 16 }}>SEND LEDGER TOTALS</div>
          <div className="grid-4" style={{ marginTop: 8 }}>
            {totals.map(([l, v, c]) => (
              <div key={l} style={{ textAlign: 'center' }}>
                <div className="mono text-xs text-muted">{l.toUpperCase()}</div>
                <div className="metric-value" style={{ fontSize: 22, color: c }}>{fmtNum(v)}</div>
              </div>
            ))}
          </div>

          <div className="card-label" style={{ marginTop: 16 }}>BY STEP</div>
          {(stats?.byStep || []).map((s) => (
            <div key={s.stepId || s.stepOrder} style={{ marginTop: 8 }}>
              <div className="flex-between mono text-xs text-muted" style={{ marginBottom: 3 }}>
                <span>Step {(s.stepOrder ?? 0) + 1}</span>
                <span>{fmtNum(s.sent)} sent · {fmtNum(s.blocked)} blocked</span>
              </div>
              <SendBar counts={s} />
            </div>
          ))}
          {(stats?.byStep || []).length === 0 && (
            <div className="text-muted text-xs" style={{ marginTop: 6 }}>No step data yet.</div>
          )}

          <div className="card-label" style={{ marginTop: 16 }}>BY CHANNEL</div>
          {(stats?.byChannel || []).map((ch) => (
            <div key={ch.channel} style={{ marginTop: 8 }}>
              <div className="flex-between mono text-xs text-muted" style={{ marginBottom: 3 }}>
                <span>{ch.channel}</span>
                <span>{fmtNum(ch.sent)} sent · {fmtNum(ch.blocked)} blocked</span>
              </div>
              <SendBar counts={ch} />
            </div>
          ))}
          {(stats?.byChannel || []).length === 0 && (
            <div className="text-muted text-xs" style={{ marginTop: 6 }}>No channel data yet.</div>
          )}
        </div>

        {/* Sends table */}
        <div className="card" style={{ padding: 0 }}>
          <div className="flex-between" style={{ padding: '12px 14px 6px' }}>
            <div className="card-label" style={{ margin: 0 }}>SENDS</div>
            <select
              className="input"
              value={sendStatus}
              onChange={(e) => { setSendStatus(e.target.value); setSendPage(0); }}
              style={{ fontSize: 12, padding: '4px 8px' }}
            >
              <option value="">All statuses</option>
              {SEND_STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="table-scroll">
          <table className="data-table">
            <thead>
              <tr>{['Customer', 'Channel', 'Status', 'Scheduled', 'Sent at'].map((h) => <th key={h}>{h}</th>)}</tr>
            </thead>
            <tbody>
              {sendRows.length === 0 && (
                <tr>
                  <td colSpan={5} className="text-muted" style={{ padding: 14, textAlign: 'center' }}>
                    No sends{sendStatus ? ` with status ${sendStatus}` : ' yet'}.
                  </td>
                </tr>
              )}
              {sendRows.map((s) => (
                <tr key={s.id}>
                  <td>{customerName(s.customerId, s.customerName)}</td>
                  <td className="mono text-xs">{s.channel}</td>
                  <td>
                    <span style={{
                      color: s.status === 'SENT' ? 'var(--success)'
                        : s.status === 'QUEUED' ? 'var(--info)'
                        : s.status === 'BLOCKED_COMPLIANCE' ? 'var(--warning)'
                        : s.status === 'FAILED' ? 'var(--danger)' : 'var(--text-tertiary)',
                      fontWeight: 600, fontSize: 12,
                    }}>
                      {s.status}
                    </span>
                    {s.blockedReason && (
                      <div className="mono text-xs text-muted">{s.blockedReason}</div>
                    )}
                  </td>
                  <td className="mono text-xs">{fmtDate(s.scheduledFor)}</td>
                  <td className="mono text-xs">{fmtDate(s.sentAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
          <div className="flex-between" style={{ padding: '10px 14px' }}>
            <button className="btn small" disabled={sends?.first !== false} onClick={() => setSendPage((p) => Math.max(0, p - 1))}>
              ← Prev
            </button>
            <span className="mono text-xs text-muted">
              page {(sends?.page ?? 0) + 1} / {Math.max(1, sends?.totalPages ?? 1)}
            </span>
            <button className="btn small" disabled={sends?.last !== false} onClick={() => setSendPage((p) => p + 1)}>
              Next →
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/* ── 3-step "New campaign" wizard ── */
function CampaignWizard({ onClose, onCreated }) {
  const { toast } = useToast();
  const [step, setStep] = useState(0);
  const [basics, setBasics] = useState({ name: '', description: '', type: 'ONE_OFF', triggerEvent: 'NONE' });
  const [audience, setAudience] = useState({ lifecycleStatus: '', minScore: '', sources: [], lastActivityBeforeDays: '' });
  const [cadence, setCadence] = useState([{ offsetDays: 0, channel: 'EMAIL', subjectOverride: '', bodyOverride: '' }]);
  const [preview, setPreview] = useState(null);

  const create     = useMutation(campaignsService.create);
  const previewSeg = useMutation(campaignsService.previewSegment);

  const segmentQuery = () => {
    const q = {};
    if (audience.lifecycleStatus) q.lifecycleStatus = audience.lifecycleStatus;
    if (audience.minScore !== '') q.minScore = Number(audience.minScore);
    if (audience.sources.length > 0) q.sources = audience.sources;
    if (audience.lastActivityBeforeDays !== '') q.lastActivityBeforeDays = Number(audience.lastActivityBeforeDays);
    return q;
  };

  const toggleSource = (src) => setAudience((a) => ({
    ...a,
    sources: a.sources.includes(src) ? a.sources.filter((s) => s !== src) : [...a.sources, src],
  }));

  const onPreview = async () => {
    try {
      const r = await previewSeg.mutate({ segmentQuery: segmentQuery() });
      setPreview(r);
    } catch (err) {
      // Never invent an audience: a failed preview must not look like a real one.
      setPreview(null);
      toast(`Preview failed: ${campaignErrorMessage(err)}`, { variant: 'danger' });
    }
  };

  const updateStepRow = (i, patch) =>
    setCadence((rows) => rows.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));

  const onSave = async () => {
    if (!basics.name.trim()) {
      toast('Campaign name is required', { variant: 'warning' });
      setStep(0);
      return;
    }
    // Every step needs message text — a step with neither a template nor a body
    // cannot be launched (backend: STEP_CONTENT_MISSING), so catch it here.
    const emptyStep = firstEmptyStep(cadence);
    if (emptyStep >= 0) {
      toast(`Step ${emptyStep + 1} needs message text before the campaign can be saved`, { variant: 'warning' });
      setStep(2);
      return;
    }
    try {
      const body = {
        name: basics.name.trim(),
        description: basics.description || null,
        type: basics.type,
        triggerEvent: basics.triggerEvent,
        segmentQuery: segmentQuery(),
        steps: cadence.map((s, i) => ({
          stepOrder: i,
          offsetDays: Number(s.offsetDays) || 0,
          channel: s.channel,
          subjectOverride: s.subjectOverride || null,
          bodyOverride: s.bodyOverride.trim(),
        })),
      };
      const created = await create.mutate(body);
      toast(`Campaign "${created?.name || basics.name}" saved as DRAFT — launch it from the table`, { variant: 'success' });
      onCreated();
    } catch (err) {
      toast(`Create failed: ${campaignErrorMessage(err)}`, { variant: 'danger' });
    }
  };

  const stepTitles = ['1 · Basics', '2 · Audience', '3 · Steps'];

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" style={{ width: 'min(760px, 95vw)' }} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <div>
            <div className="modal-title">New campaign</div>
            <div className="modal-sub mono text-xs text-muted">{stepTitles[step]}</div>
          </div>
          <button type="button" className="modal-close" onClick={onClose} aria-label="Close">✕</button>
        </div>

        <div className="modal-body" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {/* Step indicator */}
          <div style={{ display: 'flex', gap: 6 }}>
            {stepTitles.map((t, i) => (
              <span key={t} className={`pill ${i === step ? 'active' : ''}`} style={{ cursor: 'pointer' }}
                    onClick={() => setStep(i)}>
                {t}
              </span>
            ))}
          </div>

          {step === 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="form-field">
                <label>Name</label>
                <input
                  value={basics.name}
                  placeholder="e.g. Spring service reminder"
                  onChange={(e) => setBasics({ ...basics, name: e.target.value })}
                />
              </div>
              <div className="form-field">
                <label>Description</label>
                <textarea
                  rows={2}
                  value={basics.description}
                  onChange={(e) => setBasics({ ...basics, description: e.target.value })}
                />
              </div>
              <div className="grid-2">
                <div className="form-field">
                  <label>Type</label>
                  <select value={basics.type} onChange={(e) => setBasics({ ...basics, type: e.target.value })}>
                    <option value="ONE_OFF">ONE_OFF — single blast</option>
                    <option value="LIFECYCLE">LIFECYCLE — event-triggered</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>Trigger event</label>
                  <select
                    value={basics.triggerEvent}
                    onChange={(e) => setBasics({ ...basics, triggerEvent: e.target.value })}
                  >
                    {TRIGGER_EVENTS.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
            </div>
          )}

          {step === 1 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="grid-3">
                <div className="form-field">
                  <label>Lifecycle status</label>
                  <select
                    value={audience.lifecycleStatus}
                    onChange={(e) => setAudience({ ...audience, lifecycleStatus: e.target.value })}
                  >
                    <option value="">Any</option>
                    <option value="ACTIVE">Active</option>
                    <option value="DEAD_LEAD">Lost lead</option>
                  </select>
                </div>
                <div className="form-field">
                  <label>Min lead score</label>
                  <input
                    type="number" min={0} max={100}
                    value={audience.minScore}
                    onChange={(e) => setAudience({ ...audience, minScore: e.target.value })}
                  />
                </div>
                <div className="form-field">
                  <label>Last activity before (days)</label>
                  <input
                    type="number" min={0}
                    value={audience.lastActivityBeforeDays}
                    onChange={(e) => setAudience({ ...audience, lastActivityBeforeDays: e.target.value })}
                  />
                </div>
              </div>
              <div className="form-field">
                <label>Lead sources</label>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                  {LEAD_SOURCES.map((src) => (
                    <span
                      key={src}
                      className={`pill ${audience.sources.includes(src) ? 'active' : ''}`}
                      style={{ cursor: 'pointer' }}
                      onClick={() => toggleSource(src)}
                    >{src}</span>
                  ))}
                </div>
              </div>
              <div>
                <button className="btn small" disabled={previewSeg.mutating} onClick={onPreview}>
                  {previewSeg.mutating ? 'Previewing…' : 'Preview audience'}
                </button>
              </div>
              {preview && (
                <div className="card" style={{ padding: 12 }}>
                  <div className="flex-between">
                    <span className="card-label" style={{ margin: 0 }}>AUDIENCE PREVIEW</span>
                    <span className="metric-value" style={{ fontSize: 20, color: 'var(--accent-primary)' }}>
                      {fmtNum(preview.count)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                    {(preview.sample || []).map((s) => (
                      <span key={s.id} className="pill">{s.firstName} {s.lastInitial || ''}</span>
                    ))}
                    {(preview.sample || []).length === 0 && (
                      <span className="text-muted text-xs">No matching customers.</span>
                    )}
                  </div>
                  {isEmptyAudience(preview) && (
                    <div className="text-xs" style={{ marginTop: 8, color: 'var(--warning)' }}>
                      {EMPTY_AUDIENCE_MSG} A campaign with no audience cannot be launched.
                    </div>
                  )}
                  {(preview.ignoredCriteria || []).length > 0 && (
                    <div className="mono text-xs text-muted" style={{ marginTop: 6 }}>
                      Ignored criteria: {preview.ignoredCriteria.join(', ')}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}

          {step === 2 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div className="mono text-xs text-muted">
                Merge fields like {'{{first_name}}'} are substituted per customer at send time.
              </div>
              {cadence.map((row, i) => (
                <div key={i} className="card" style={{ padding: 12 }}>
                  <div className="flex-between" style={{ marginBottom: 8 }}>
                    <span className="card-label" style={{ margin: 0 }}>STEP {i + 1}</span>
                    {cadence.length > 1 && (
                      <button className="btn small danger"
                              onClick={() => setCadence((rows) => rows.filter((_, idx) => idx !== i))}>
                        Remove
                      </button>
                    )}
                  </div>
                  <div className="grid-3" style={{ marginBottom: 8 }}>
                    <div className="form-field">
                      <label>Offset days</label>
                      <input
                        type="number" min={0} max={365}
                        value={row.offsetDays}
                        onChange={(e) => updateStepRow(i, { offsetDays: e.target.value })}
                      />
                    </div>
                    <div className="form-field">
                      <label>Channel</label>
                      <select value={row.channel} onChange={(e) => updateStepRow(i, { channel: e.target.value })}>
                        {STEP_CHANNELS.map((c) => <option key={c} value={c}>{c}</option>)}
                      </select>
                    </div>
                    <div className="form-field">
                      <label>Subject override</label>
                      <input
                        value={row.subjectOverride}
                        placeholder="Email subject (optional)"
                        onChange={(e) => updateStepRow(i, { subjectOverride: e.target.value })}
                      />
                    </div>
                  </div>
                  <div className="form-field">
                    <label>Message text (required)</label>
                    <textarea
                      rows={3}
                      value={row.bodyOverride}
                      placeholder="Hi {{first_name}}, your next service is due…"
                      onChange={(e) => updateStepRow(i, { bodyOverride: e.target.value })}
                    />
                  </div>
                </div>
              ))}
              <div>
                <button
                  className="btn small"
                  onClick={() => setCadence((rows) => [...rows,
                    { offsetDays: 0, channel: 'EMAIL', subjectOverride: '', bodyOverride: '' }])}
                >+ Add step</button>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer flex-between" style={{ padding: '14px 20px' }}>
          <button className="btn small" disabled={step === 0} onClick={() => setStep((s) => s - 1)}>
            ← Back
          </button>
          {step < 2 ? (
            <button className="btn small primary" onClick={() => setStep((s) => s + 1)}>Next →</button>
          ) : (
            <button className="btn small primary" disabled={create.mutating} onClick={onSave}>
              {create.mutating ? 'Saving…' : 'Save as DRAFT'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
